import React from 'react';
import logoBranco from '../../img/logo-legis-branco.svg';
import { Icone, NomeIcone } from './LegisPocket.icones';
import { legisPocketCss } from './LegisPocket.styles';

/**
 * LegisPocket — página pública de apresentação do Legisnote.
 *
 * Rota: /legispocket (ver LegisPocket.config.ts e o desvio em App.tsx).
 * Não depende de autenticação, do PalletContext nem de qualquer chamada à API.
 * É material informativo: nenhum formulário, nenhuma captura de dados.
 */

// ------------------------------------------------------------------ conteúdo

const NUMEROS = [
    { valor: 'Legislativo + Executivo', texto: 'Proposições, tramitações, emendas, discursos, agendas e atos oficiais em uma base só.' },
    { valor: 'Federal, Estadual e Municipal', texto: 'Câmara, Senado, Congresso, assembleias, câmaras municipais e Diário Oficial.' },
    { valor: 'Monitoramento 24/7', texto: 'Agentes de IA acompanhando o que se move nos temas do seu negócio, sem pausa.' },
    { valor: 'Alerta na hora do fato', texto: 'A notificação chega onde você já está: WhatsApp, e-mail e plataforma.' },
];

const FONTES = [
    { icone: 'instituicao' as NomeIcone, texto: 'Câmara dos Deputados' },
    { icone: 'instituicao' as NomeIcone, texto: 'Senado Federal' },
    { icone: 'balanca' as NomeIcone, texto: 'Congresso Nacional' },
    { icone: 'documento' as NomeIcone, texto: 'Diário Oficial da União' },
    { icone: 'mapa' as NomeIcone, texto: 'Assembleias Legislativas' },
    { icone: 'mapa' as NomeIcone, texto: 'Câmaras Municipais' },
    { icone: 'conversa' as NomeIcone, texto: 'Mídia e redes' },
];

const PILARES = [
    {
        icone: 'documento' as NomeIcone,
        titulo: 'Registrar',
        texto: 'Seus temas, mercados, posicionamentos e stakeholders viram estrutura: cada interesse do seu negócio fica cadastrado, versionado e rastreável dentro da plataforma.',
    },
    {
        icone: 'radar' as NomeIcone,
        titulo: 'Monitorar',
        texto: 'A partir desse cadastro, a plataforma varre continuamente o Legislativo e o Executivo nas três esferas e separa o que é ruído do que é relevante para você.',
    },
    {
        icone: 'grafico' as NomeIcone,
        titulo: 'Analisar',
        texto: 'Prioridade, impacto financeiro, risco, convergência de parlamentares e evolução dos temas — a leitura estratégica pronta para a reunião de diretoria.',
    },
    {
        icone: 'raio' as NomeIcone,
        titulo: 'Executar',
        texto: 'Ações preparatórias, notas técnicas, posicionamentos por unidade de negócio e esforço colaborativo: da informação à decisão sem trocar de ferramenta.',
    },
];

const AGENTES = [
    {
        icone: 'alvo' as NomeIcone,
        titulo: 'Agente de Interesse e Mercados',
        texto: 'Lê o inteiro teor de cada proposição, emenda e ato publicado e verifica se ele toca os seus temas e os mercados em que você atua — devolvendo o trecho exato que gerou o alerta.',
        tag: 'Relevância com evidência',
    },
    {
        icone: 'lupa' as NomeIcone,
        titulo: 'Agente Jabuti',
        texto: 'Procura dispositivos estranhos ao objeto do texto — os famosos jabutis — inseridos ao longo da tramitação. O que passaria despercebido em centenas de páginas chega até você.',
        tag: 'Risco escondido',
    },
    {
        icone: 'camadas' as NomeIcone,
        titulo: 'Agente de Sumarização',
        texto: 'Transforma inteiro teor, pareceres, tramitações e discursos em resumos executivos objetivos. Você entende o que mudou em segundos, não em uma tarde de leitura.',
        tag: 'Leitura em segundos',
    },
    {
        icone: 'balanca' as NomeIcone,
        titulo: 'Agente de Classificação',
        texto: 'Classifica tema, prioridade e possível impacto de cada matéria antes de ela chegar à sua mesa. A triagem já vem feita, com critério consistente e auditável.',
        tag: 'Triagem automática',
    },
    {
        icone: 'documento' as NomeIcone,
        titulo: 'Agente de Nota Técnica',
        texto: 'Prepara a minuta da nota técnica a partir do histórico da matéria e dos posicionamentos já registrados pela sua equipe. O especialista revisa e aprova em vez de começar do zero.',
        tag: 'Trabalho adiantado',
    },
    {
        icone: 'conversa' as NomeIcone,
        titulo: 'SmartTalk',
        texto: 'Converse com a sua base em linguagem natural: pergunte sobre uma proposição, um parlamentar, um tema ou um histórico e receba a resposta com a fonte no acervo.',
        tag: 'Pergunte e receba',
    },
];

const GOVERNANCA = [
    'Controle de habilitação por cliente e por usuário',
    'Consumo de IA medido e acompanhado',
    'Kill switch para interromper o processamento a qualquer momento',
    'Toda resposta ancorada em documento oficial',
];

const NOTIFICACOES = [
    { titulo: 'Movimento nas suas matérias', texto: 'nova proposição, atualização, tramitação, emenda relacionada, relator definido, apensação e proposições apensadas.' },
    { titulo: 'Agenda que te afeta', texto: 'pauta legislativa, sessões, agenda corporativa cadastrada, acontecimentos, cancelamentos e alterações.' },
    { titulo: 'Citações do seu nome e dos seus temas', texto: 'em proposições, tramitações, emendas, discursos, no Diário Oficial da União, na mídia e nas redes.' },
    { titulo: 'Prazos e pendências do time', texto: 'nota técnica a vencer, ação preparatória atribuída, posicionamento pendente, escalonamento e demanda no prazo final.' },
    { titulo: 'Relacionamento com parlamentares', texto: 'aniversário, mudança de partido e discursos sobre os assuntos que você acompanha.' },
];

const ENTREGAS_DASHBOARD = [
    { icone: 'relogio' as NomeIcone, titulo: 'Pendências e prazos', texto: 'O que está parado, com quem está e quando vence — antes de virar problema.' },
    { icone: 'grafico' as NomeIcone, titulo: 'Impacto financeiro', texto: 'Dimensão econômica das matérias acompanhadas, consolidada por tema e por unidade.' },
    { icone: 'alvo' as NomeIcone, titulo: 'Prioridade', texto: 'Distribuição do portfólio por criticidade, para alocar esforço onde o risco está.' },
    { icone: 'pessoas' as NomeIcone, titulo: 'Mapa de parlamentares', texto: 'Convergentes, divergentes e neutros nos seus temas, com histórico de posições.' },
    { icone: 'camadas' as NomeIcone, titulo: 'Posicionamento por unidade', texto: 'Cada área da empresa registra sua leitura e o consolidado vira posição institucional.' },
    { icone: 'agenda' as NomeIcone, titulo: 'Agenda e relatórios', texto: 'A semana à frente e os relatórios prontos para levar à diretoria e ao board.' },
];

const PASSOS = [
    {
        numero: '01',
        titulo: 'Você registra os seus interesses',
        texto: 'Temas corporativos, mercados de atuação, palavras-chave, parlamentares e stakeholders. Uma vez configurado, é a base de tudo o que vem depois.',
    },
    {
        numero: '02',
        titulo: 'Os agentes trabalham por você',
        texto: 'Enquanto você toca a agenda do dia, a plataforma lê, classifica, resume e cruza tudo o que é publicado no Legislativo e no Executivo das três esferas.',
    },
    {
        numero: '03',
        titulo: 'Você recebe só o que importa — e age',
        texto: 'O alerta chega no WhatsApp, no e-mail e na plataforma, já com contexto e resumo. Daí você decide, delega, posiciona e registra. Sem correr atrás.',
    },
];

const NAVEGACAO = [
    { id: 'plataforma', rotulo: 'Plataforma' },
    { id: 'agentes', rotulo: 'Agentes de IA' },
    { id: 'notificacoes', rotulo: 'Notificações' },
    { id: 'dashboard', rotulo: 'Dashboard' },
    { id: 'como-funciona', rotulo: 'Como funciona' },
];

// ------------------------------------------------------------------ componente

export default function LegisPocket(): JSX.Element {
    const [headerSolido, setHeaderSolido] = React.useState<boolean>(false);
    const [menuAberto, setMenuAberto] = React.useState<boolean>(false);
    const raizRef = React.useRef<HTMLDivElement>(null);
    const headerRef = React.useRef<HTMLElement>(null);

    // título/description próprios da landing, restaurados ao sair da página
    React.useEffect(() => {
        const tituloAnterior = document.title;
        document.title = 'Legisnote — Inteligência de dados para Relacionamento Institucional e Governamental';

        const meta = document.querySelector('meta[name="description"]');
        const descricaoAnterior = meta?.getAttribute('content') || '';
        meta?.setAttribute(
            'content',
            'Plataforma de inteligência de dados para RIG: registro, monitoramento, análise estratégica e agentes autônomos de IA acompanhando o Legislativo e o Executivo nas esferas federal, estadual e municipal.',
        );

        return () => {
            document.title = tituloAnterior;
            meta?.setAttribute('content', descricaoAnterior);
        };
    }, []);

    // header ganha fundo sólido depois do topo
    React.useEffect(() => {
        const aoRolar = () => setHeaderSolido(window.scrollY > 24);
        aoRolar();
        window.addEventListener('scroll', aoRolar, { passive: true });
        return () => window.removeEventListener('scroll', aoRolar);
    }, []);

    // entrada suave das seções ao entrar na viewport
    React.useEffect(() => {
        const alvos = raizRef.current?.querySelectorAll('.lp-reveal');
        if (!alvos || !alvos.length) return;

        if (typeof IntersectionObserver === 'undefined') {
            alvos.forEach(el => el.classList.add('lp-visivel'));
            return;
        }

        const observador = new IntersectionObserver(
            entradas => {
                entradas.forEach(entrada => {
                    if (entrada.isIntersecting) {
                        entrada.target.classList.add('lp-visivel');
                        observador.unobserve(entrada.target);
                    }
                });
            },
            { rootMargin: '0px 0px -8% 0px', threshold: 0.08 },
        );

        alvos.forEach(el => observador.observe(el));
        return () => observador.disconnect();
    }, []);

    // fecha o menu compacto ao voltar para uma largura em que ele não existe,
    // evitando o painel preso aberto ao girar o aparelho
    React.useEffect(() => {
        if (!menuAberto) return;

        const aoRedimensionar = () => {
            if (window.innerWidth > 900) setMenuAberto(false);
        };
        const aoTeclar = (evento: KeyboardEvent) => {
            if (evento.key === 'Escape') setMenuAberto(false);
        };

        window.addEventListener('resize', aoRedimensionar);
        window.addEventListener('keydown', aoTeclar);
        return () => {
            window.removeEventListener('resize', aoRedimensionar);
            window.removeEventListener('keydown', aoTeclar);
        };
    }, [menuAberto]);

    // o offset da âncora acompanha a altura real do header, que muda por breakpoint
    const irPara = (id: string) => (evento: React.MouseEvent) => {
        evento.preventDefault();
        setMenuAberto(false);
        const destino = document.getElementById(id);
        if (!destino) return;
        const alturaHeader = headerRef.current?.getBoundingClientRect().height || 68;
        const topo = destino.getBoundingClientRect().top + window.pageYOffset - alturaHeader - 12;
        window.scrollTo({ top: Math.max(topo, 0), behavior: 'smooth' });
    };

    return (
        <div className="lp-root" ref={raizRef}>
            <style dangerouslySetInnerHTML={{ __html: legisPocketCss }} />

            {/* ------------------------------------------------------ header */}
            <header className={`lp-header${headerSolido ? ' lp-header--solido' : ''}`} ref={headerRef}>
                <div className="lp-container lp-header-inner">
                    <div className="lp-header-logo">
                        <img src={logoBranco} alt="Legisnote" />
                    </div>
                    <nav className="lp-header-nav">
                        {NAVEGACAO.filter(item => item.id !== 'agentes').map(item => (
                            <a key={item.id} href={`#${item.id}`} onClick={irPara(item.id)}>{item.rotulo}</a>
                        ))}
                    </nav>
                    <div className="lp-header-acao">
                        <a className="lp-btn lp-btn--claro lp-btn--pequeno" href="#agentes" onClick={irPara('agentes')}>
                            Agentes de IA
                        </a>
                    </div>
                    <button
                        type="button"
                        className={`lp-menu-btn${menuAberto ? ' lp-menu-btn--aberto' : ''}`}
                        aria-label={menuAberto ? 'Fechar menu' : 'Abrir menu'}
                        aria-expanded={menuAberto}
                        aria-controls="lp-menu"
                        onClick={() => setMenuAberto(aberto => !aberto)}
                    >
                        <span />
                    </button>
                </div>

                {menuAberto ? (
                    <div className="lp-menu" id="lp-menu">
                        <nav className="lp-container lp-menu-lista">
                            {NAVEGACAO.map(item => (
                                <a key={item.id} href={`#${item.id}`} onClick={irPara(item.id)}>{item.rotulo}</a>
                            ))}
                        </nav>
                    </div>
                ) : null}
            </header>

            {/* ------------------------------------------------------ hero */}
            <section className="lp-hero">
                <div className="lp-container lp-hero-inner">
                    <div className="lp-hero-grid">
                        <div>
                            <span className="lp-eyebrow lp-eyebrow--dark">
                                <i className="lp-eyebrow-dot" />
                                Inteligência de dados para RIG
                            </span>

                            <h1>
                                Enquanto você cuida da estratégia,{' '}
                                <span className="lp-hero-grifo">o Legis trabalha por você</span>.
                            </h1>

                            <p className="lp-lead lp-lead--dark lp-hero-sub">
                                O Legisnote é a plataforma de inteligência de dados para Relacionamento
                                Institucional e Governamental. Registro, monitoramento, análise estratégica e
                                execução autônoma — com agentes de IA conectados aos interesses do seu negócio
                                e do seu mercado, 24 horas por dia.
                            </p>

                            <div className="lp-hero-ctas">
                                <a className="lp-btn lp-btn--primario" href="#agentes" onClick={irPara('agentes')}>
                                    Conhecer os agentes de IA
                                    <Icone nome="seta" />
                                </a>
                                <a className="lp-btn lp-btn--claro" href="#plataforma" onClick={irPara('plataforma')}>
                                    Ver a plataforma
                                </a>
                            </div>

                            <p className="lp-hero-nota" style={{ marginTop: "20px" }}>
                                <Icone nome="escudo" />
                                Dados de fontes oficiais, com rastreabilidade da origem em cada informação.
                            </p>
                        </div>

                        <div className="lp-painel" aria-hidden="true">
                            <div className="lp-painel-topo">
                                <span className="lp-painel-titulo">
                                    <i className="lp-painel-pulso" />
                                    Monitoramento ativo
                                </span>
                                <span className="lp-painel-tag">Agora</span>
                            </div>

                            <div className="lp-painel-lista">
                                <div className="lp-painel-item">
                                    <span className="lp-painel-icone"><Icone nome="alvo" /></span>
                                    <span className="lp-painel-corpo">
                                        <strong>Nova proposição no seu tema</strong>
                                        <span>Meio ambiente · aderência ao mercado monitorado · trecho destacado</span>
                                    </span>
                                </div>
                                <div className="lp-painel-item">
                                    <span className="lp-painel-icone"><Icone nome="lupa" /></span>
                                    <span className="lp-painel-corpo">
                                        <strong>Jabuti identificado em substitutivo</strong>
                                        <span>Dispositivo estranho ao objeto detectado na última versão do texto</span>
                                    </span>
                                </div>
                                <div className="lp-painel-item">
                                    <span className="lp-painel-icone"><Icone nome="camadas" /></span>
                                    <span className="lp-painel-corpo">
                                        <strong>Resumo executivo pronto</strong>
                                        <span>Parecer do relator sumarizado · impacto e pontos de atenção</span>
                                    </span>
                                </div>
                                <div className="lp-painel-item">
                                    <span className="lp-painel-icone"><Icone nome="agenda" /></span>
                                    <span className="lp-painel-corpo">
                                        <strong>Pauta da semana atualizada</strong>
                                        <span>Duas matérias suas entraram na ordem do dia da comissão</span>
                                    </span>
                                </div>
                            </div>

                            <div className="lp-painel-rodape">
                                <Icone nome="agente" />
                                Processado pelos agentes de IA do Legisnote, sem intervenção manual.
                            </div>
                        </div>
                    </div>

                    <div className="lp-numeros">
                        {NUMEROS.map(item => (
                            <div className="lp-numero" key={item.valor}>
                                <strong>{item.valor}</strong>
                                <span>{item.texto}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ------------------------------------------------------ fontes */}
            <div className="lp-faixa">
                <div className="lp-container">
                    <p className="lp-faixa-titulo">Cobertura contínua das fontes que decidem o seu jogo</p>
                    <div className="lp-chips">
                        {FONTES.map(fonte => (
                            <span className="lp-chip" key={fonte.texto}>
                                <Icone nome={fonte.icone} />
                                {fonte.texto}
                            </span>
                        ))}
                    </div>
                </div>
            </div>

            {/* ------------------------------------------------------ pilares */}
            <section className="lp-section" id="plataforma">
                <div className="lp-container">
                    <div className="lp-section-head lp-section-head--centro lp-reveal">
                        <span className="lp-eyebrow">A plataforma</span>
                        <h2 className="lp-h2">
                            Quatro movimentos que sustentam <span className="lp-grifo">todo o trabalho de RIG</span>
                        </h2>
                        <p className="lp-lead">
                            Do cadastro do interesse à execução da resposta, tudo acontece no mesmo lugar —
                            com histórico, responsáveis e evidência de cada passo.
                        </p>
                    </div>

                    <div className="lp-grid lp-grid--4 lp-reveal">
                        {PILARES.map(pilar => (
                            <article className="lp-card" key={pilar.titulo}>
                                <span className="lp-card-icone"><Icone nome={pilar.icone} /></span>
                                <h3>{pilar.titulo}</h3>
                                <p>{pilar.texto}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* ------------------------------------------------------ agentes de IA */}
            <section className="lp-section lp-section--escuro" id="agentes">
                <div className="lp-container">
                    <div className="lp-section-head lp-section-head--centro lp-reveal">
                        <span className="lp-eyebrow lp-eyebrow--dark">
                            <i className="lp-eyebrow-dot" />
                            Agentes autônomos de IA
                        </span>
                        <h2 className="lp-h2">Você tem uma equipe de IA lendo o poder público por você</h2>
                        <p className="lp-lead lp-lead--dark">
                            Não é um chat solto em cima dos seus documentos. São agentes especializados,
                            rodando de forma autônoma sobre a base oficial e sobre os interesses que você
                            registrou — cada um com uma função clara dentro do processo de RIG.
                        </p>
                    </div>

                    <div className="lp-grid lp-grid--3 lp-reveal">
                        {AGENTES.map(agente => (
                            <article className="lp-agente" key={agente.titulo}>
                                <div className="lp-agente-topo">
                                    <span className="lp-agente-icone"><Icone nome={agente.icone} /></span>
                                    <h3>{agente.titulo}</h3>
                                </div>
                                <p>{agente.texto}</p>
                                <span className="lp-agente-tag">{agente.tag}</span>
                            </article>
                        ))}
                    </div>

                    <div className="lp-governanca lp-reveal">
                        {GOVERNANCA.map(item => (
                            <span className="lp-governanca-item" key={item}>
                                <Icone nome="check" />
                                {item}
                            </span>
                        ))}
                    </div>
                </div>
            </section>

            {/* ------------------------------------------------------ notificações */}
            <section className="lp-section" id="notificacoes">
                <div className="lp-container">
                    <div className="lp-split">
                        <div className="lp-reveal">
                            <span className="lp-eyebrow">Notificações de interesse</span>
                            <h2 className="lp-h2">
                                O alerta certo, <span className="lp-grifo">na hora do fato</span>,
                                onde você já está
                            </h2>
                            <p className="lp-lead">
                                Nada de abrir a plataforma para descobrir o que aconteceu. O Legisnote avisa
                                você por WhatsApp, e-mail e dentro da plataforma — sempre com contexto,
                                resumo e link para a fonte.
                            </p>

                            <ul className="lp-lista-check">
                                {NOTIFICACOES.map(item => (
                                    <li key={item.titulo}>
                                        <Icone nome="check" />
                                        <span><strong>{item.titulo}:</strong> {item.texto}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="lp-reveal">
                            <div className="lp-zap" aria-hidden="true">
                                <div className="lp-zap-topo">
                                    <span className="lp-zap-avatar">LN</span>
                                    <span>
                                        <strong>Legisnote</strong>
                                        <span>online · monitorando</span>
                                    </span>
                                </div>
                                <div className="lp-zap-msgs">
                                    <div className="lp-zap-msg">
                                        <span className="lp-zap-selo">Alta prioridade</span> -
                                        <b> PL 1.234/2026</b> foi apensado à matéria que você acompanha em
                                        tributário. O relator já foi designado.
                                        <span className="lp-zap-hora">08:12</span>
                                    </div>
                                    <div className="lp-zap-msg">
                                        <b>Resumo do parecer</b> disponível: três pontos de atenção para o seu
                                        setor e um dispositivo com impacto financeiro estimado.
                                        <span className="lp-zap-hora">08:13</span>
                                    </div>
                                    <div className="lp-zap-msg">
                                        <span className="lp-zap-selo">Citação </span>
                                        Sua empresa foi citada em discurso na Comissão de Assuntos Econômicos.
                                        <span className="lp-zap-hora">14:47</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ------------------------------------------------------ dashboard */}
            <section className="lp-section lp-section--cinza" id="dashboard">
                <div className="lp-container">
                    <div className="lp-section-head lp-reveal">
                        <span className="lp-eyebrow">Dashboard e análise estratégica</span>
                        <h2 className="lp-h2">A leitura da sua operação em uma tela</h2>
                        <p className="lp-lead">
                            O dashboard consolida o que a equipe precisa acompanhar e o que a diretoria
                            precisa saber — atualizado à medida que os agentes processam o dia.
                        </p>
                    </div>

                    <div className="lp-dash lp-reveal">
                        {ENTREGAS_DASHBOARD.map(item => (
                            <article className="lp-dash-item" key={item.titulo}>
                                <h4><Icone nome={item.icone} />{item.titulo}</h4>
                                <p>{item.texto}</p>
                            </article>
                        ))}
                    </div>

                    <div className="lp-grid lp-grid--2 lp-grid--espacado lp-reveal">
                        <article className="lp-card">
                            <span className="lp-card-icone"><Icone nome="lampada" /></span>
                            <h3>Insights</h3>
                            <p>
                                Além do acompanhamento, o Legisnote gera leituras: temas em aceleração,
                                comportamento de parlamentares nos seus assuntos, movimentos que se repetem e
                                sinais que antecipam o próximo passo. É a análise que normalmente exigiria
                                horas de garimpo, entregue pronta.
                            </p>
                        </article>
                        <article className="lp-card">
                            <span className="lp-card-icone"><Icone nome="pessoas" /></span>
                            <h3>Trabalho colaborativo</h3>
                            <p>
                                Ações preparatórias, notas técnicas, esforço colaborativo e posicionamento por
                                unidade de negócio. Cada área contribui no seu campo, com prazo e responsável
                                definidos, e a posição institucional se consolida com histórico completo.
                            </p>
                        </article>
                    </div>
                </div>
            </section>

            {/* ------------------------------------------------------ como funciona */}
            <section className="lp-section" id="como-funciona">
                <div className="lp-container">
                    <div className="lp-section-head lp-section-head--centro lp-reveal">
                        <span className="lp-eyebrow">Como funciona</span>
                        <h2 className="lp-h2">Simples de começar. Difícil de abrir mão.</h2>
                        <p className="lp-lead">
                            A configuração inicial é feita com o nosso time. A partir dela, a plataforma
                            passa a trabalhar sozinha pelos seus interesses.
                        </p>
                    </div>

                    <div className="lp-grid lp-grid--3 lp-reveal">
                        {PASSOS.map(passo => (
                            <article className="lp-card lp-card--passo" key={passo.numero}>
                                <span className="lp-card-passo">{passo.numero}</span>
                                <h3>{passo.titulo}</h3>
                                <p>{passo.texto}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* ------------------------------------------------------ confiança */}
            <section className="lp-section lp-section--escuro">
                <div className="lp-container">
                    <div className="lp-citacao lp-reveal">
                        <span className="lp-eyebrow lp-eyebrow--dark">Confiança</span>
                        <blockquote>
                            “A tecnologia só vale se você puder confiar nela sem checar duas vezes. Por isso
                            cada alerta do Legisnote aponta a fonte oficial, o trecho e o caminho que a
                            informação percorreu.”
                        </blockquote>
                        <span>Fontes oficiais · rastreabilidade ponta a ponta · controle de acesso por perfil · login corporativo (SSO)</span>
                    </div>
                </div>
            </section>

            {/* ------------------------------------------------------ cta final */}
            <section className="lp-section lp-section--cinza">
                <div className="lp-container">
                    <div className="lp-cta lp-reveal">
                        <h2>Deixe o Legis trabalhar. Você decide.</h2>
                        <p>
                            Monitorar o poder público deixou de ser uma corrida contra o volume de publicações.
                            Com os interesses do seu negócio registrados, os agentes leem, classificam e avisam —
                            e a sua equipe volta a gastar tempo com o que só ela pode fazer: articular, posicionar
                            e decidir.
                        </p>
                        <p className="lp-cta-nota">
                            Estamos conectados aos seus interesses. Você fica despreocupado.
                        </p>
                    </div>
                </div>
            </section>

            {/* ------------------------------------------------------ footer */}
            <footer className="lp-footer">
                <div className="lp-container lp-footer-inner">
                    <img src={logoBranco} alt="Legisnote" />
                    <nav className="lp-footer-links">
                        {NAVEGACAO.map(item => (
                            <a key={item.id} href={`#${item.id}`} onClick={irPara(item.id)}>{item.rotulo}</a>
                        ))}
                    </nav>
                    <div className="lp-footer-copy">
                        Legisnote — plataforma de inteligência de dados para Relacionamento Institucional e
                        Governamental. © {new Date().getFullYear()} Spread Tecnologia. Todos os direitos reservados.
                    </div>
                </div>
            </footer>
        </div>
    );
}

export { LegisPocket };

/**
 * Tela de espera enquanto /config responde se a rota está liberada.
 * Usa o mesmo roxo do topo da página para não haver flash branco antes do hero.
 */
export function LegisPocketCarregando(): JSX.Element {
    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                display: 'grid',
                placeItems: 'center',
                background: 'linear-gradient(165deg, #2B0950 0%, #1E0736 58%, #130322 100%)',
            }}
        >
            <span
                style={{
                    width: '38px',
                    height: '38px',
                    borderRadius: '50%',
                    border: '3px solid rgba(255, 255, 255, 0.18)',
                    borderTopColor: '#C9A6FF',
                    animation: 'lp-girando .9s linear infinite',
                }}
            />
            <style dangerouslySetInnerHTML={{ __html: '@keyframes lp-girando { to { transform: rotate(360deg); } }' }} />
        </div>
    );
}
